import { h } from 'vue';

import type {
  SurveyButtonTheme,
  SurveyDisplayMode
} from '@/components/Survey/Survey.vue';
import Survey from '@/components/Survey/Survey.vue';
import { mountCoreVueApp } from '@/entrypoints/shared/core-vue-app';

const mountId = 'js-survey';

// Valid enum values for runtime validation
const VALID_DISPLAY_MODES: SurveyDisplayMode[] = ['inline', 'modal'];
const VALID_BUTTON_THEMES: SurveyButtonTheme[] = [
  'primary',
  'primary-light',
  'primary-transparent',
  'white',
  'gray',
  'gray-dark',
  'danger'
];

// Validation functions
const validateDisplayMode = (value: string | undefined): SurveyDisplayMode => {
  if (value && VALID_DISPLAY_MODES.includes(value as SurveyDisplayMode)) {
    return value as SurveyDisplayMode;
  }
  return 'inline'; // Default fallback
};

const validateButtonTheme = (value: string | undefined): SurveyButtonTheme => {
  if (value && VALID_BUTTON_THEMES.includes(value as SurveyButtonTheme)) {
    return value as SurveyButtonTheme;
  }
  return 'primary-transparent'; // Default fallback
};

const initSurvey = async () => {
  const mountElement = document.getElementById(mountId);

  if (!mountElement) {
    return;
  }

  // Read configuration from data attributes
  const dataset = mountElement.dataset;
  const subject = dataset.subject;
  const action = dataset.action;
  const subjectId = dataset.subjectId;
  const title = dataset.title;
  const prompt = dataset.prompt;
  const debug = dataset.debug === 'true';

  // Validate and set display mode and button theme with proper fallbacks
  const validatedMode = validateDisplayMode(dataset.mode);
  const validatedButtonTheme = validateButtonTheme(dataset.buttonTheme);

  // Capture custom trigger HTML if present
  const customTriggerHtml = mountElement.innerHTML.trim();

  // Validate required props
  if (!subject || !action) {
    console.error(
      '[Survey] Missing required data attributes: data-subject and data-action are required'
    );
    return;
  }

  // Create survey props object with explicit typing and validated values
  const surveyProps: {
    subject: string;
    action: string;
    mode: SurveyDisplayMode;
    subjectId?: string;
    title?: string;
    prompt?: string;
    buttonTheme?: SurveyButtonTheme;
    debug?: boolean;
    customTriggerHtml?: string;
  } = {
    subject,
    action,
    mode: validatedMode,
    buttonTheme: validatedButtonTheme,
    ...(subjectId && { subjectId }),
    ...(title && { title }),
    ...(prompt && { prompt }),
    ...(debug && { debug }),
    ...(customTriggerHtml && { customTriggerHtml })
  };

  // Create a render function that renders the Survey component with props from data attributes
  const SurveyWrapper = () => h(Survey, surveyProps);

  await mountCoreVueApp(SurveyWrapper, mountElement);
};

initSurvey();
